import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { PageHeader, StatusBadge } from "@/components/Common";
import PendingApprovals from "@/components/PendingApprovals";
import FactoryToday from "@/components/FactoryToday";
import EnhancedKpisStrip from "@/components/EnhancedKpisStrip";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Warning, CheckCircle, Timer, Snowflake, Package, Truck, ArrowUpRight,
  ShieldWarning, Factory, ChartLineUp, TrendUp, TrendDown, Sun, Moon, Clock
} from "@phosphor-icons/react";

// IST shift countdown: Day 06:00–18:00, Night 18:00–06:00
function useShiftCountdown() {
  const [state, setState] = useState(() => computeIstShiftEnd());
  useEffect(() => {
    const t = setInterval(() => setState(computeIstShiftEnd()), 30000);
    return () => clearInterval(t);
  }, []);
  return state;
}

function computeIstShiftEnd() {
  // Current IST time = UTC + 5:30
  const nowUtcMs = Date.now();
  const istMs = nowUtcMs + 5.5 * 3600 * 1000;
  const ist = new Date(istMs);
  const h = ist.getUTCHours();
  const isDay = h >= 6 && h < 18;
  // Build IST end-boundary as UTC ms
  const y = ist.getUTCFullYear();
  const mo = ist.getUTCMonth();
  const d = ist.getUTCDate();
  let endIstMs;
  if (isDay) {
    endIstMs = Date.UTC(y, mo, d, 18, 0, 0);
  } else if (h < 6) {
    endIstMs = Date.UTC(y, mo, d, 6, 0, 0);
  } else {
    endIstMs = Date.UTC(y, mo, d + 1, 6, 0, 0);
  }
  const remainMs = Math.max(0, endIstMs - istMs);
  const totalMin = Math.floor(remainMs / 60000);
  const hh = Math.floor(totalMin / 60);
  const mm = totalMin % 60;
  return { shift: isDay ? "day" : "night", hh, mm, label: `${hh}h ${mm}m` };
}

const RAG_BG = { green: "bg-emerald-500", yellow: "bg-amber-500", red: "bg-red-500" };
const RAG_TXT = { green: "text-emerald-600", yellow: "text-amber-600", red: "text-red-600" };
const SEV_BG = { red: "bg-red-50 border-red-200", yellow: "bg-amber-50 border-amber-200", info: "bg-blue-50 border-blue-200", success: "bg-emerald-50 border-emerald-200" };
const SEV_ICON = { red: "text-red-600", yellow: "text-amber-600", info: "text-blue-600", success: "text-emerald-600" };

function KPI({ label, value, sub, tone = "default", testid, hint }) {
  const toneCls = { default: "text-foreground", primary: "text-primary", success: "text-emerald-600", warning: "text-amber-600", danger: "text-red-600" }[tone];
  return (
    <Card className="shadow-none" data-testid={testid}>
      <CardContent className="p-4">
        <div className="overline mb-2">{label}</div>
        <div className={`text-metric text-3xl ${toneCls}`}>{value}</div>
        {sub && <div className="text-xs text-muted-foreground mt-1.5 truncate">{sub}</div>}
        {hint && <div className="text-[10px] text-muted-foreground/70 mt-0.5">{hint}</div>}
      </CardContent>
    </Card>
  );
}

function MachineTile({ m }) {
  const running = m.status === "Running" || m.assigned_count > 0;
  const eta = m.expected_finish ? new Date(m.expected_finish) : null;
  return (
    <Card className="shadow-none" data-testid={`command-machine-${m.code}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-md bg-primary/10 text-primary flex items-center justify-center">
              <Snowflake weight="duotone" size={20}/>
            </div>
            <div>
              <div className="font-display font-bold text-base leading-tight">{m.name}</div>
              <div className="overline mt-0.5">{m.capacity_kg}kg · {m.trays} trays</div>
            </div>
          </div>
          <StatusBadge status={running ? "Running" : m.status}/>
        </div>

        <div className="space-y-2.5 mb-4">
          <div>
            <div className="flex justify-between text-[11px] mb-1">
              <span className="font-semibold">Capacity</span>
              <span className="mono">{m.load_kg}/{m.capacity_kg} kg</span>
            </div>
            <Progress value={m.utilization_pct} className="h-1.5"/>
          </div>
          <div>
            <div className="flex justify-between text-[11px] mb-1">
              <span className="font-semibold">Trays</span>
              <span className="mono">{m.load_trays}/{m.trays}</span>
            </div>
            <Progress value={m.tray_utilization_pct} className="h-1.5"/>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 border-t border-border pt-3">
          <div>
            <div className="overline">Remaining</div>
            <div className="text-metric text-sm">{m.remaining_kg}kg</div>
          </div>
          <div>
            <div className="overline">Batches</div>
            <div className="text-metric text-sm">{m.assigned_count}</div>
          </div>
          <div>
            <div className="overline flex items-center gap-1"><Timer size={10}/>ETA</div>
            <div className="text-[11px] font-semibold mono truncate">
              {eta ? eta.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [shift, setShift] = useState("auto");
  const countdown = useShiftCountdown();

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get(`/dashboard?shift=${shift}`);
        setD(data);
      } catch {}
    };
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [shift]);

  if (!d) return <div className="p-8 text-muted-foreground">Loading Command Centre…</div>;

  const pt = d.production_today;
  const tp = d.today_production;
  const k = d.kpis;
  const inv = d.inventory_status;

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        subtitle={`Live · Factory Command Centre · Now on ${d.current_shift === "day" ? "Day" : "Night"} Shift`}
        title="Chericious Ops Command"
        actions={
          <div className="flex flex-wrap gap-2 items-center">
            <Tabs value={shift} onValueChange={setShift}>
              <TabsList>
                <TabsTrigger value="auto" data-testid="dash-shift-auto">
                  {d.current_shift === "day" ? <Sun size={12} className="mr-1"/> : <Moon size={12} className="mr-1"/>}
                  Current shift
                </TabsTrigger>
                <TabsTrigger value="all" data-testid="dash-shift-all">All</TabsTrigger>
                <TabsTrigger value="day" data-testid="dash-shift-day"><Sun size={12} className="mr-1"/>Day</TabsTrigger>
                <TabsTrigger value="night" data-testid="dash-shift-night"><Moon size={12} className="mr-1"/>Night</TabsTrigger>
              </TabsList>
            </Tabs>
            <div
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/40 px-2.5 py-1 text-[11px] font-medium text-muted-foreground"
              title={`Current IST shift ends in ${countdown.label}`}
              data-testid="shift-countdown-chip"
            >
              <Clock size={12} weight="regular"/>
              <span>Shift ends in</span>
              <span className="mono font-semibold text-foreground/80" data-testid="shift-countdown-value">{countdown.label}</span>
            </div>
            <Button variant="outline" asChild data-testid="cta-kanban"><Link to="/kanban"><Factory size={16} className="mr-1.5"/>Live Workflow</Link></Button>
            <Button asChild data-testid="cta-planner"><Link to="/planning"><ChartLineUp size={16} className="mr-1.5"/>Smart Planner</Link></Button>
          </div>
        }
      />

      <PendingApprovals/>

      <FactoryToday/>

      <EnhancedKpisStrip/>

      {/* Production Today */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <KPI label="Planned" value={pt.planned} sub="Batches today" testid="pt-planned"/>
        <KPI label="Running" value={pt.running} tone="primary" sub="In progress" testid="pt-running"/>
        <KPI label="Completed" value={pt.completed} tone="success" sub="Today" testid="pt-completed"/>
        <KPI label="Delayed" value={pt.delayed} tone={pt.delayed > 0 ? "danger" : "default"} sub="Beyond SLA" testid="pt-delayed"/>
      </div>

      {/* Today's Production vs Target */}
      <Card className="shadow-none mb-6">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline">Today's Production</div>
              <div className="font-display font-bold text-lg">Target vs Actual (kg)</div>
            </div>
            <div className="text-right">
              <div className={`text-metric text-3xl ${tp.completion_pct >= 90 ? "text-emerald-600" : tp.completion_pct >= 60 ? "text-amber-600" : "text-red-600"}`}>{tp.completion_pct}%</div>
              <div className="overline">Completion</div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-6 mb-4">
            <div><div className="overline">Target</div><div className="text-metric text-2xl">{tp.target_kg}</div></div>
            <div><div className="overline">Actual</div><div className="text-metric text-2xl text-primary">{tp.actual_kg}</div></div>
            <div><div className="overline">Difference</div><div className={`text-metric text-2xl ${tp.difference_kg >= 0 ? "text-emerald-600" : "text-red-600"}`}>{tp.difference_kg >= 0 ? "+" : ""}{tp.difference_kg}</div></div>
          </div>
          <Progress value={tp.completion_pct} className="h-2"/>
        </CardContent>
      </Card>

      {/* Machine Status */}
      <div className="mb-6">
        <div className="overline mb-3">Freeze Dryer Status</div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {d.machines.map(m => <MachineTile key={m.id} m={m}/>)}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <KPI label="Yield %" value={`${k.yield_pct}%`} tone="success" testid="kpi-yield"/>
        <KPI label="Machine Util" value={`${k.machine_utilization_pct}%`} tone="primary" testid="kpi-util"/>
        <KPI label="Efficiency" value={`${k.efficiency_pct}%`} testid="kpi-efficiency"/>
        <KPI label="Avg Batch Time" value={`${k.avg_batch_time_h}h`} sub="Prep → Pack" testid="kpi-cycle"/>
        <KPI label="Order Fulfillment" value={`${k.order_fulfillment_pct}%`} testid="kpi-fulfillment"/>
        <KPI label="Rejected" value={k.rejected_qty} tone={k.rejected_qty > 0 ? "warning" : "default"} testid="kpi-rejected"/>
        <KPI label="Wastage %" value={`${k.wastage_pct}%`} tone={k.wastage_pct > 5 ? "danger" : "default"} testid="kpi-wastage"/>
        <KPI label="OEE" value={`${k.oee}%`} tone="primary" testid="kpi-oee" hint="Availability × Performance × Quality"/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Inventory RAG */}
        <Card className="shadow-none">
          <CardContent className="p-5">
            <div className="overline mb-4">Inventory Status</div>
            <div className="grid grid-cols-3 gap-3 mb-4">
              {["green", "yellow", "red"].map(k => (
                <div key={k} className="text-center p-3 rounded-md border border-border">
                  <div className={`w-3 h-3 rounded-full ${RAG_BG[k]} mx-auto mb-2`}/>
                  <div className={`text-metric text-2xl ${RAG_TXT[k]}`}>{inv[k]}</div>
                  <div className="overline mt-1">{k === "green" ? "Healthy" : k === "yellow" ? "Warning" : "Critical"}</div>
                </div>
              ))}
            </div>
            <div className="border-t border-border pt-3">
              <div className="overline mb-2">Attention Items</div>
              <div className="space-y-2">
                {d.low_stock_items.slice(0, 5).map(m => (
                  <div key={m.id} className="flex justify-between items-center text-sm">
                    <div>
                      <div className="font-semibold text-xs">{m.name}</div>
                      <div className="text-[10px] text-muted-foreground">{m.category}</div>
                    </div>
                    <div className={`mono text-xs ${m.stock <= m.min_stock ? "text-red-600" : "text-amber-600"}`}>
                      {m.stock} {m.unit}
                    </div>
                  </div>
                ))}
                {d.low_stock_items.length === 0 && <div className="text-xs text-muted-foreground py-4 text-center">All materials healthy</div>}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Alerts */}
        <Card className="shadow-none lg:col-span-2">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="overline flex items-center gap-2"><ShieldWarning size={14}/>Today's Alerts</div>
              <span className="text-xs text-muted-foreground">{d.alerts.length} active</span>
            </div>
            {d.alerts.length === 0 ? (
              <div className="text-sm text-muted-foreground text-center py-8">All systems normal · No alerts</div>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {d.alerts.map((a, i) => (
                  <div key={i} className={`p-3 rounded-md border ${SEV_BG[a.severity] || SEV_BG.info}`} data-testid={`alert-${a.type}-${i}`}>
                    <div className="flex items-start gap-3">
                      <Warning size={16} className={SEV_ICON[a.severity] || "text-muted-foreground"} weight="fill"/>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-foreground">{a.title}</div>
                        <div className="text-xs text-muted-foreground truncate">{a.detail}</div>
                      </div>
                      <span className={`text-[10px] font-bold uppercase ${SEV_ICON[a.severity]}`}>{a.severity}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
